import { useState, useEffect } from 'react';
import { availabilityApi } from '../api/availability';
import { useAuth } from '../contexts/AuthContext';

/**
 * useAvailabilityContexts Hook - Availability Contexts State Management
 *
 * Centralizes availability contexts state management and API integration
 * for AvailabilityCalendarPage
 *
 * ARCHITECTURAL CHANGE (2025):
 * - Now uses availability_contexts (range-based, fully editable)
 * - Replaces availability_overrides approach (individual day overrides)
 * - Contexts are stored as ranges (no grouping logic needed)
 * - Dates AND reason are fully editable
 *
 * Features:
 * - Availability contexts list state management
 * - API integration (load, create, update, delete contexts)
 * - Calendar range selection state
 * - Auto-refresh after mutations
 * - Success messages auto-clear after 3 seconds
 */
const useAvailabilityContexts = () => {
    const { user } = useAuth();

    // State
    const [blockedDates, setBlockedDates] = useState([]);
    const [selectedRange, setSelectedRange] = useState(null);
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [blockType, setBlockType] = useState('holiday');

    // Load blocked dates on mount
    useEffect(() => {
        loadBlockedDates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Load availability contexts from API
     * Filters to block-type contexts (holiday, leave, personal)
     */
    const loadBlockedDates = async () => {
        if (!user?.organisationId) {
            setError('Switch to your instructor account to manage blocked dates');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError('');

            const contexts = await availabilityApi.getContexts();

            const blocked = contexts.filter(ctx =>
                ctx.context === 'holiday' || ctx.context === 'leave' || ctx.context === 'personal'
            );

            const transformedDates = blocked.map(ctx => ({
                id: ctx.id,
                startDate: ctx.startDate,
                endDate: ctx.endDate,
                reason: ctx.reason || 'No reason provided',
                context: ctx.context,
                mode: ctx.mode,
                color: ctx.color || '#999999'
            }));

            setBlockedDates(transformedDates);
        } catch (err) {
            console.error('Error loading blocked dates:', err);
            setError('Failed to load blocked dates. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle blocking selected date range
     * Creates a new availability context (holiday or leave)
     */
    const handleBlockDates = async () => {
        if (!selectedRange || !selectedRange.start || !selectedRange.end) {
            setError('Please select a date range');
            return;
        }

        if (!reason.trim()) {
            setError('Please provide a reason for blocking these dates');
            return;
        }

        try {
            setError('');
            setSuccessMessage('');

            const contextData = {
                startDate: selectedRange.start,
                endDate: selectedRange.end,
                context: blockType,
                mode: 'closed',
                reason: reason.trim()
            };

            const result = await availabilityApi.createContext(contextData);

            if (result) {
                setSuccessMessage('Successfully blocked dates');

                // Clear form
                setSelectedRange(null);
                setReason('');

                // Refresh list
                await loadBlockedDates();

                // Auto-clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            console.error('Error blocking dates:', err);
            setError(err.message || 'Failed to block dates. Please try again.');
        }
    };

    /**
     * Handle editing availability context
     * Fully editable: can change dates AND reason
     * @param {string} contextId - Context ID to update
     * @param {object} updates - Fields to update (startDate, endDate, reason)
     */
    const handleEditContext = async (contextId, updates) => {
        try {
            setError('');
            setSuccessMessage('');

            // Update context (fully editable - dates + reason)
            await availabilityApi.updateContext(contextId, updates);

            setSuccessMessage('Successfully updated blocked dates');

            // Refresh list
            await loadBlockedDates();

            // Auto-clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error editing blocked dates:', err);
            setError(err.message || 'Failed to edit blocked dates. Please try again.');
        }
    };

    /**
     * Handle deleting availability context
     * @param {string} contextId - Context ID to delete
     * NOTE: Parent component should show confirmation modal before calling this
     */
    const handleDeleteContext = async (contextId) => {
        try {
            setError('');
            setSuccessMessage('');

            await availabilityApi.deleteContext(contextId);

            setSuccessMessage('Blocked date deleted successfully');

            // Refresh list
            await loadBlockedDates();

            // Auto-clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error deleting blocked date:', err);
            setError(err.message || 'Failed to delete blocked date. Please try again.');
        }
    };

    /**
     * Handle calendar range selection
     * @param {object} range - {start: 'YYYY-MM-DD', end: 'YYYY-MM-DD'}
     */
    const handleSelectRange = (range) => {
        setSelectedRange(range);
        setError('');
        setSuccessMessage('');
    };

    return {
        // State
        blockedDates,
        selectedRange,
        reason,
        blockType,
        isLoading,
        error,
        successMessage,

        // Setters
        setSelectedRange: handleSelectRange,
        setReason,
        setBlockType,

        // Functions
        loadBlockedDates,
        handleBlockDates,
        handleEditContext,
        handleDeleteContext
    };
};

export default useAvailabilityContexts;
